import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Search, Users, MessageSquare, ArrowRight, HeartHandshake, UserCheck, CheckCircle, Briefcase, GraduationCap, Loader2 } from "lucide-react";
import { Bot, Sparkles } from "lucide-react";
import Layout from "../components/layout/Layout";
import ExpertCard from "../components/ui/ExpertCard";
import { useExperts } from "../hooks/useExperts";

export default function HomePage() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { experts: dbExperts, loading: expertsLoading } = useExperts();

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/experts?q=${encodeURIComponent(searchQuery)}`);
  };

  const featuredExperts = dbExperts.filter(e => e.available).slice(0, 3);

  const stats = [
    { value: "150+", label: "Участников" },
    { value: "2000+", label: "Обсуждений" },
    { value: "4.9", label: "Средняя оценка" },
    { value: "98%", label: "Рекомендуют" }
  ];

  const howItWorks = [
    {
      icon: Bot,
      title: "Обстучите с AI-напарником",
      description: "Опишите свою задачу — AI-партнёр поможет структурировать мысли и подсветит слепые зоны"
    },
    {
      icon: MessageSquare,
      title: "Подключите эксперта",
      description: "Когда нужна глубина — найдите практика из реального бизнеса, который проходил то же самое"
    },
    {
      icon: Sparkles,
      title: "Получите результат",
      description: "Конкретный план действий: AI фиксирует выводы, эксперт даёт рекомендации из практики"
    }
  ];

  const values = [
    "AI-напарник доступен 24/7 — обстучите идею в любой момент",
    "Живые эксперты и практики с реальным опытом в бизнесе",
    "Честная обратная связь, а не красивые слова",
    "Конкретные рекомендации, проверенные практикой",
    "AI фиксирует выводы — ничего не потеряется",
    "Конфиденциальность каждого разговора"
  ];

  return (
    <Layout>
      <section id="home_hero" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 bg-neutral-950" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-primary-800/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent-800/6 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <p className="text-lg font-extralight text-primary-400 mb-6 tracking-wide">
              Ваш напарник в бизнес-решениях
            </p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extralight text-neutral-100 mb-6 leading-tight text-balance">
              Вы не одиноки
              <br />
              <span className="text-primary-400 font-light">в своих вопросах</span>
            </h1>

            <p className="text-xl lg:text-2xl font-extralight text-neutral-400 mb-10 max-w-2xl leading-relaxed">
              Обстучите идею с личным AI-партнёром в любое время, а когда нужна глубина — подключайте экспертов и практиков из реального бизнеса, которые уже через это прошли.
            </p>

            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-xl mb-10">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                <input
                  type="text"
                  placeholder="Маркетинг, стратегия, HR..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-md bg-neutral-900/60 border border-neutral-800/50 text-neutral-100 text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="px-8 py-4 rounded-md bg-primary-700 hover:bg-primary-600 text-white text-lg font-extralight transition-colors flex items-center justify-center gap-2"
              >
                Найти
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-6 text-lg font-extralight text-neutral-500">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary-500" />
                <span>Эксперты</span>
              </div>
              <div className="w-px h-4 bg-neutral-700" />
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-accent-500" />
                <span>Практики из бизнеса</span>
              </div>
              <div className="w-px h-4 bg-neutral-700" />
              <span className="text-neutral-300">150+ участников</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="home_stats" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="text-3xl lg:text-4xl font-light text-neutral-200 mb-2">
                  {stat.value}
                </div>
                <div className="text-lg font-extralight text-neutral-500">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="home_community" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg font-extralight text-primary-400 mb-4 tracking-wide">
                Почему это работает
              </p>
              <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-6 text-balance">
                AI-напарник + живой эксперт
              </h2>
              <p className="text-xl font-extralight text-neutral-400 mb-8 leading-relaxed">
                AI помогает структурировать мысли и подсветить слепые зоны в любое время. А когда нужна глубина — подключайте практика из реального бизнеса, который проходил то же самое. Вместе они закрывают любую бизнес-задачу.
              </p>
              <ul className="space-y-4">
                {values.map((value, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" />
                    <span className="text-lg font-extralight text-neutral-400">{value}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Bot className="w-8 h-8 text-primary-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">AI-напарник</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Доступен 24/7, структурирует мысли и подсвечивает риски
                    </p>
                  </div>
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <UserCheck className="w-8 h-8 text-accent-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Эксперты</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Консультанты с глубокой экспертизой в своей области
                    </p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Briefcase className="w-8 h-8 text-warm-500 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Практики из бизнеса</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Предприниматели, которые прошли через те же вызовы
                    </p>
                  </div>
                  <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/30">
                    <Sparkles className="w-8 h-8 text-primary-400 mb-4" />
                    <h3 className="text-xl font-light text-neutral-200 mb-2">Результат</h3>
                    <p className="text-lg font-extralight text-neutral-500">
                      Конкретный план действий, а не абстрактные советы
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="home_how-it-works" className="py-20 lg:py-28 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-4">
              Как это работает
            </h2>
            <p className="text-xl font-extralight text-neutral-500 max-w-2xl mx-auto">
              Три шага до разговора, который может изменить ваш подход
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative p-8 rounded-md bg-neutral-900/40 border border-neutral-800/30 text-center"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-md bg-primary-700 flex items-center justify-center text-white text-lg font-extralight">
                  {index + 1}
                </div>
                <div className="w-14 h-14 mx-auto mb-6 rounded-md bg-primary-800/20 flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-primary-400" />
                </div>
                <h3 className="text-xl font-light text-neutral-200 mb-3">{step.title}</h3>
                <p className="text-lg font-extralight text-neutral-500">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="home_featured-experts" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12"
          >
            <div>
              <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-4">
                Люди, которые помогут
              </h2>
              <p className="text-xl font-extralight text-neutral-500">
                Эксперты и практики с проверенным опытом
              </p>
            </div>
            <Link
              to="/experts"
              className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 text-lg font-extralight transition-colors"
            >
              Все участники
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertsLoading ? (
              <div className="col-span-full text-center py-12">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin mx-auto" />
              </div>
            ) : (
              featuredExperts.map((expert) => (
                <ExpertCard key={expert.id} expert={expert} />
              ))
            )}
          </div>
        </div>
      </section>

      <section id="home_cta" className="py-20 lg:py-28 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-extralight text-neutral-100 mb-6 text-balance">
              Вы не одиноки. Начните прямо сейчас.
            </h2>
            <p className="text-xl font-extralight text-neutral-400 mb-8 max-w-2xl mx-auto">
              Обстучите идею с AI-напарником или подключите живого эксперта — выбирайте, что нужно именно сейчас
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/experts"
                className="px-8 py-4 rounded-md bg-primary-700 hover:bg-primary-600 text-white text-lg font-extralight transition-colors flex items-center gap-2"
              >
                Попробовать AI-напарника
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}